import { Text } from '../../../../components/Text';
import { formatChatsDate } from '../../../../utils/datemaker';
import PieChartTimer from '../../PieChartTimer';
import { useState, useEffect } from 'react';
import { formatMobNumber, maskMobNumber } from '../../../../utils/commonUtils';
import permissions from '../../../../roles/permissions';
import modulesName from '../../../../roles/modulesName';
import { useSelector } from 'react-redux';
import { Tooltip } from 'primereact/tooltip';
import { ReactComponent as AudioIcon } from '../../../../svg/audio.svg';

function ContactChatInfo({ contact = null }) {
  const [currentTime, setCurrentTime] = useState(new Date().getTime());
  const { accesskey } = useSelector((state) => state.authenticate);
  const { user } = useSelector((state) => state.wcaEnqChats);

  // Update the time every second for PieChartTimer
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Determine if the current agent has permission to view the full number
  const getContactPhone = (contactInfo) =>
    accesskey[modulesName.CUSTOMER]?.includes(permissions.READ)
      ? formatMobNumber(contactInfo?.phone)
      : maskMobNumber(contactInfo?.phone);

  // Capitalize the first letter of a name
  const capitalizeName = (name) => name?.charAt(0)?.toUpperCase() + name?.substring(1);

  // Determine display name: WhatsApp name or phone number
  const getContactName = (contactInfo) =>
    contactInfo?.whatsappName
      ? capitalizeName(contactInfo.whatsappName)
      : formatMobNumber(contactInfo?.phone);

  // Convert timestamp (if provided as seconds) to date
  const formatTimestamp = (ts) => {
    if (!ts) return '';
    // Handle Unix timestamp (seconds)
    if (typeof ts === 'number' && ts < 1e12) ts *= 1000;
    return formatChatsDate(ts);
  };

  // 🧩 Handle truncating long messages
  function handleChatLength(msg) {
    let text = '';
    if (typeof msg === 'string') text = msg;
    else if (msg && typeof msg === 'object' && typeof msg.content === 'string') text = msg?.content;

    if (text) {
      const words = text.split(' ');
      return words?.length > 5 ? words?.slice(0, 5)?.join(' ') + '…' : text;
    }
    return '';
  }

  // 🧩 Render the last message with icon
  function renderLastMessage(contact) {
    if (!contact?.lastMessage) return null;
    const { message } = contact.lastMessage;
    const type = message?.type || contact.lastMessage?.messageType;

    const lastMessageTypeIcon = (messageType) => {
      switch (messageType) {
        case 'image':
          return <i className="pi pi-camera" style={{ marginRight: 4 }} />;
        case 'video':
          return <i className="pi pi-video" style={{ marginRight: 4 }} />;
        case 'audio':
          return (
            <>
              <AudioIcon style={{ marginRight: 4 }} />
              <span>audio</span>
            </>
          );
        case 'file':
          return <i className="pi pi-file" style={{ marginRight: 4 }} />;
        default:
          return null;
      }
    };

    // Figure out text body if available
    const textBody = message?.text?.body || message?.content || '';

    return (
      <Text type="small-text" cust="ch-limit-30" style={{ opacity: 0.7 }}>
        {lastMessageTypeIcon(type)}
        {type === 'text' && handleChatLength(textBody)}
      </Text>
    );
  }

  return (
    <>
      {contact && (
        <div className="userChatInfo flex flex-column gap-1">
          <Tooltip target=".block-target-icon" />
          <div className="flex justify-content-between">
            {/* LEFT SIDE: Name + unread count */}
            <div className="flex align-items-center gap-2">
              <Text type="sub-heading" cust="ch-limit-30">
                <span>{getContactName(contact)}</span>
              </Text>

              {/* Show unread badge only if > 0 and not currently selected */}
              {user?.phone !== contact?.phone && contact?.unreadMessages > 0 && (
                <span className="dot-unread">{contact.unreadMessages}</span>
              )}
            </div>

            {/* RIGHT SIDE: Updated time + status + timer */}

            <div className="flex align-items-center gap-2">
              <Text type="small-text">{formatTimestamp(contact?.updatedAt)}</Text>
              <div className="ml-2 flex justify-content-center align-items-center gap-2">
                {/* If contact is inactive, show block icon */}
                {contact?.isOptOut && (
                  <i
                    className="pi pi-ban block-target-icon"
                    data-pr-tooltip="Blocked"
                    data-pr-position="top"
                    style={{ color: 'red', fontSize: '19px', cursor: 'default' }}
                  />
                )}

                {/* Timer circle */}
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    border: '1px solid rgba(0, 0, 0, 0.7)',
                    borderRadius: '50%',
                    padding: '2px',
                  }}
                >
                  <PieChartTimer
                    currentTime={currentTime}
                    startTimestamps={contact?.lastMessageTimestamp * 1000}
                    duration={24 * 60 * 60 * 1000}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Second line: phone + status */}
          {/* <div className="flex justify-content-between align-items-center">
            <Text type="small-text" style={{ opacity: '0.6' }} cust="ch-limit-30">
              {formatMobNumber(contact?.phone)}
            </Text>
          </div> */}
          <div className="flex justify-content-between align-items-center">
            {renderLastMessage(contact)}
          </div>
        </div>
      )}
    </>
  );
}

export default ContactChatInfo;
